import React from 'react';
import { Activity, Bot, Calendar, Code, Flag, Globe, Headset, Mail, MessageSquarePlus, Monitor, Sparkles, User, Users } from 'lucide-react';
import { FilterDef, FilterOption, SortDef } from '@/components/shared/filters';

interface SimpleOption {
  value: string;
  label: string;
}

interface BuildSessionsFiltersArgs {
  agentOptions: SimpleOption[];
  leadOptions: SimpleOption[];
  repOptions: SimpleOption[];
  /** Hide the Contact filter when the list is already scoped to one lead. */
  includeContact: boolean;
}

export const SESSIONS_DATE_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Dates' },
  { value: 'today', label: 'Today' },
  { value: 'last3days', label: 'Last 3 Days' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'last90days', label: 'Last 90 Days' },
  { value: 'lastYear', label: 'Last Year' },
];

/**
 * Sessions filter registry. URL params are kept identical to the previous inline
 * implementation so existing links and ExportSessionsButton keep working:
 *   date_filter, agent_id, lead_id, human_agent_ids (repeated), is_active, conversation_type.
 */
export function buildSessionsFilters({
  agentOptions,
  leadOptions,
  repOptions,
  includeContact,
}: BuildSessionsFiltersArgs): FilterDef[] {
  const filters: FilterDef[] = [
    {
      id: 'date',
      label: 'Date',
      icon: <Calendar className="h-3.5 w-3.5" />,
      type: 'select',
      urlParam: 'date_filter',
      pinned: true,
      options: SESSIONS_DATE_OPTIONS,
    },
    {
      id: 'agent',
      label: 'Agent',
      icon: <Bot className="h-3.5 w-3.5" />,
      type: 'select',
      urlParam: 'agent_id',
      pinned: true,
      searchable: true,
      options: agentOptions,
    },
  ];

  if (includeContact) {
    filters.push({
      id: 'contact',
      label: 'Contact',
      icon: <User className="h-3.5 w-3.5" />,
      type: 'select',
      urlParam: 'lead_id',
      searchable: true,
      options: leadOptions,
    });
  }

  filters.push(
    {
      id: 'reps',
      label: 'Reps',
      icon: <Users className="h-3.5 w-3.5" />,
      type: 'multiselect',
      urlParam: 'human_agent_ids',
      searchable: true,
      options: repOptions,
    },
    {
      id: 'status',
      label: 'Status',
      icon: <Activity className="h-3.5 w-3.5" />,
      type: 'select',
      urlParam: 'is_active',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      toParam: (value) => ({
        is_active: value === 'active' ? 'true' : value === 'inactive' ? 'false' : null,
      }),
      fromParam: (params) => {
        const isActive = params.get('is_active');
        return isActive === 'true' ? 'active' : isActive === 'false' ? 'inactive' : 'all';
      },
    },
    {
      id: 'source',
      label: 'Source',
      icon: <Globe className="h-3.5 w-3.5" />,
      type: 'select',
      urlParam: 'conversation_type',
      options: [
        { value: 'all', label: 'All Sources' },
        { value: 'lead', label: 'Website', icon: <Globe className="h-3.5 w-3.5 text-green-500" /> },
        { value: 'app', label: 'App', icon: <Monitor className="h-3.5 w-3.5 text-blue-500" /> },
        { value: 'both', label: 'Website + App' },
        { value: 'email', label: 'Email', icon: <Mail className="h-3.5 w-3.5 text-amber-500" /> },
        { value: 'gateway', label: 'AI Gateway', icon: <Bot className="h-3.5 w-3.5 text-cyan-500" /> },
      ],
      // Website/App map to the legacy conversation_type param; Email/Gateway
      // are transports and map to the channel param (conversations.channel).
      toParam: (value) => {
        if (value === 'all' || typeof value !== 'string') {
          return { conversation_type: null, channel: null };
        }
        if (value === 'email' || value === 'gateway') {
          return { conversation_type: null, channel: value };
        }
        return { conversation_type: value, channel: null };
      },
      fromParam: (params) => params.get('channel') || params.get('conversation_type') || 'all',
    },
    {
      id: 'initiated_by',
      label: 'Started By',
      icon: <MessageSquarePlus className="h-3.5 w-3.5" />,
      type: 'select',
      urlParam: 'initiated_by',
      options: [
        { value: 'all', label: 'Anyone' },
        { value: 'visitor', label: 'Visitor', icon: <User className="h-3.5 w-3.5" /> },
        { value: 'ai', label: 'AI', icon: <Sparkles className="h-3.5 w-3.5 text-purple-500" /> },
        { value: 'human', label: 'Rep', icon: <Headset className="h-3.5 w-3.5 text-emerald-500" /> },
        { value: 'sdk', label: 'SDK', icon: <Code className="h-3.5 w-3.5 text-orange-500" /> },
        { value: 'ai_agent', label: 'AI Agent', icon: <Bot className="h-3.5 w-3.5 text-cyan-500" /> },
      ],
      // The backend rejects unknown values, so 'all' must map to no param.
      toParam: (value) => ({ initiated_by: value === 'all' || typeof value !== 'string' ? null : value }),
      fromParam: (params) => params.get('initiated_by') || 'all',
    },
    {
      id: 'moderation',
      label: 'Moderation',
      icon: <Flag className="h-3.5 w-3.5" />,
      type: 'select',
      urlParam: 'moderation',
      options: [
        { value: 'all', label: 'All Sessions' },
        { value: 'flagged', label: 'Flagged', icon: <Flag className="h-3.5 w-3.5 text-orange-500" /> },
      ],
      toParam: (value) => ({ moderation: value === 'flagged' ? 'flagged' : null }),
      fromParam: (params) => (params.get('moderation') === 'flagged' ? 'flagged' : 'all'),
    },
  );

  return filters;
}

/**
 * Server-sortable columns. The default order (active first, then most recent) is
 * a composite the backend applies when no sort is set, so there's no default
 * field here — the Sort badge reads "Sort" until the user picks one.
 */
export const SESSIONS_SORTS: SortDef[] = [
  { id: 'created_at', label: 'Session Started', field: 'created_at', defaultOrder: 'desc' },
  { id: 'last_activity', label: 'Last Active', field: 'last_activity', defaultOrder: 'desc' },
  { id: 'status', label: 'Status', field: 'status', defaultOrder: 'asc' },
];
